import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Users, Server, Globe, ArrowRight, Shield, Zap, Sparkles, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import './SolutionsPage.css';

const solutionsList = [
    {
        id: "ems",
        number: "01",
        icon: <Users size={32} strokeWidth={1.5} />,
        title: "Employee Management System",
        subtitle: "A unified platform to manage workforce, payroll, and performance.",
        description: "Simplify how you manage your people. Our Employee Management System brings HR tasks, attendance tracking, automated payroll calculation, leave approvals, and employee performance tracking into a single, intuitive interface.",
        benefits: [
            "Attendance Tracking: Automated check-ins, biometrics or web portals.",
            "Automated Payroll: Direct calculation of hours, tax, deductions, and payroll.",
            "Leave Management: Self-service portal for applications, approvals, and balance checks.",
            "Performance Tracking: KPI dashboards and employee reviews."
        ],
        badge: "Internal Operations"
    },
    {
        id: "custom-systems",
        number: "02",
        icon: <Server size={32} strokeWidth={1.5} />,
        title: "Custom Business Systems",
        description: "Standard software forces you to change your processes. We build software that conforms entirely to your unique business workflows. Perfect for custom logistics, inventory tracking, field operations, and core business management.",
        subtitle: "Purpose-built software designed around your unique workflows.",
        benefits: [
            "Tailored ERP & CRM: Custom pipelines tailored for your exact sales and supply flows.",
            "Operations & Logistics: Track fleets, shipments, assets, or inventories in real-time.",
            "Workflow Automation: Eliminate spreadsheets and automate manual approval gates.",
            "Actionable Dashboards: Unified reports giving leadership immediate decision-making visibility."
        ],
        badge: "Business Operations"
    },
    {
        id: "digital-platforms",
        number: "03",
        icon: <Globe size={32} strokeWidth={1.5} />,
        title: "Digital Platforms",
        description: "Build a product that connects your organization directly with customers, partners, or communities. We design and launch scalable SaaS platforms, online marketplaces, and mobile-first portals that scale to millions of users.",
        subtitle: "Web and mobile platforms designed to connect with customers and communities.",
        benefits: [
            "SaaS Architectures: Multi-tenant billing, subscription gates, and user hierarchies.",
            "Customer Portals: Self-service dashboards, ticketing, and account management.",
            "E-commerce Ecosystems: Multi-vendor checkout, secure payments, and localized APIs.",
            "Community Platforms: Dynamic social spaces, forums, and direct messaging."
        ],
        badge: "Customer Engagement"
    }
];

const SolutionsPage = () => {
    return (
        <div className="solutions-page">
            <Helmet>
                <title>Our Solutions | Kodebuild | Purpose-Built Software</title>
                <meta name="description" content="Discover Kodebuild's business products and solutions: Employee Management System, Custom ERPs, and high-performance customer-facing Digital Platforms." />
                <link rel="canonical" href="https://kodebuildtech.com/solutions" />
                {/* Open Graph */}
                <meta property="og:title" content="Our Solutions | Kodebuild | Purpose-Built Software" />
                <meta property="og:description" content="Employee Management Systems, Custom Business ERPs, and scalable Digital Platforms — purpose-built software by Kodebuild." />
                <meta property="og:url" content="https://kodebuildtech.com/solutions" />
                <meta property="og:type" content="website" />
                <meta property="og:image" content="https://kodebuildtech.com/Logo/kodebuild_png.png" />
                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Our Solutions | Kodebuild | Purpose-Built Software" />
                <meta name="twitter:description" content="Employee Management Systems, Custom Business ERPs, and scalable Digital Platforms — purpose-built software by Kodebuild." />
                <meta name="twitter:image" content="https://kodebuildtech.com/Logo/kodebuild_png.png" />
            </Helmet>

            {/* Solutions Hero */}
            <section className="solutions-hero">
                <div className="solutions-hero-glow" />
                <div className="container">
                    <motion.span
                        className="section-badge"
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        Solutions Ecosystem
                    </motion.span>
                    <motion.h1
                        className="solutions-hero-title"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                    >
                        What We <span className="text-gradient">Build</span>
                    </motion.h1>
                    <motion.p
                        className="solutions-hero-subtitle"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        We build dedicated business systems and customer-facing software products that solve operational challenges and connect you with your audience.
                    </motion.p>
                </div>
            </section>

            {/* Solutions Section Detail */}
            <section className="solutions-detail-list">
                <div className="container">
                    <div className="solutions-grid-detail">
                        {solutionsList.map((sol, index) => (
                            <motion.div
                                key={sol.id}
                                className="sol-detail-card glass"
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ duration: 0.7, delay: index * 0.15 }}
                            >
                                <div className="sol-card-main-info">
                                    <div className="sol-card-badge-row">
                                        <span className="sol-badge-tag">{sol.badge}</span>
                                        <span className="sol-card-num">{sol.number}</span>
                                    </div>
                                    <div className="sol-card-header">
                                        <div className="sol-icon-box">{sol.icon}</div>
                                        <div>
                                            <h2 className="sol-card-title">{sol.title}</h2>
                                            <p className="sol-card-subtitle">{sol.subtitle}</p>
                                        </div>
                                    </div>
                                    <p className="sol-card-desc">{sol.description}</p>
                                </div>

                                <div className="sol-card-benefits">
                                    <h3>Key Capabilities:</h3>
                                    <div className="benefits-list">
                                        {sol.benefits.map((benefit, bIndex) => {
                                            const [title, desc] = benefit.split(':');
                                            return (
                                                <div key={bIndex} className="benefit-item-detail">
                                                    <div className="benefit-bullet-wrap">
                                                        <Check size={16} className="benefit-icon" />
                                                    </div>
                                                    <div>
                                                        <strong>{title}:</strong> {desc}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Technology Alignment */}
            <section className="solutions-why-build">
                <div className="container">
                    <div className="solutions-why-grid">
                        <div className="solutions-why-content">
                            <span className="section-badge">The Advantage</span>
                            <h2>Why Choose a Custom Solution?</h2>
                            <p>Off-the-shelf software introduces recurring subscription fees, rigid templates, and forces your teams to warp their workflows to fit a generic structure. Custom business software acts as a capital asset that scales with your growth.</p>
                            
                            <div className="adv-list">
                                <div className="adv-item">
                                    <div className="adv-icon-wrap"><Sparkles size={20} /></div>
                                    <div>
                                        <h4>Zero Monthly Seat Fees</h4>
                                        <p>You own the IP. No seat fees when onboarding new employees or users.</p>
                                    </div>
                                </div>
                                <div className="adv-item">
                                    <div className="adv-icon-wrap"><Zap size={20} /></div>
                                    <div>
                                        <h4>Infinite Customization</h4>
                                        <p>Conforms to your workflows, checklists, reporting requirements, and local regulations.</p>
                                    </div>
                                </div>
                                <div className="adv-item">
                                    <div className="adv-icon-wrap"><Shield size={20} /></div>
                                    <div>
                                        <h4>Host on Secure Local Server or Private Cloud</h4>
                                        <p>Maintain total control over database compliance, offline accessibility, and data custody.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="solutions-why-illustration glass">
                            <div className="flow-lines-graphic">
                                <div className="flow-node flow-node-1">
                                    <span>Workforce Data</span>
                                </div>
                                <div className="flow-arrow-down" />
                                <div className="flow-node flow-node-core">
                                    <span>KodeBuild Core System</span>
                                </div>
                                <div className="flow-arrow-down" />
                                <div className="flow-group-nodes">
                                    <div className="flow-subnode">Automated Payroll</div>
                                    <div className="flow-subnode">Attendance Reports</div>
                                    <div className="flow-subnode">Unified Dashboard</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="solutions-cta">
                <div className="container">
                    <div className="sol-cta-inner glass">
                        <h2>Have a complex business challenge?</h2>
                        <p>Let's design and build a custom product that modernizes operations and drives operational capacity.</p>
                        <Link to="/contact" className="btn-primary glow-hover">
                            Discuss Your Solution <ArrowRight size={18} style={{ marginLeft: '8px' }} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default SolutionsPage;
